/**
 * Guard for user- or feed-supplied URLs rendered as anchor `href` attributes.
 *
 * React does NOT block `javascript:` URLs in `<a href>`, so any href sourced
 * from workspace data (library items, excerpts) or external feeds (RSS/Atom)
 * must be scheme-checked before rendering to prevent stored XSS.
 *
 * Allowed schemes: http, https, mailto.
 * Returns the trimmed URL when safe, otherwise `null`.
 */
export function safeHref(
  url: string | null | undefined,
): string | null {
  if (!url) return null;
  const trimmed = String(url).trim();
  if (!trimmed) return null;

  // Reject control chars / whitespace inside a would-be scheme (defeats
  // bypasses like "java\tscript:alert(1)").
  // eslint-disable-next-line no-control-regex
  const cleaned = trimmed.replace(/[\u0000-\u001F\u007F]/g, "");

  // Allow protocol-relative and root-relative paths (always same-origin safe).
  if (cleaned.startsWith("//") || cleaned.startsWith("/")) return cleaned;

  // Anything without a scheme delimiter is treated as a relative path.
  if (!/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(cleaned)) return cleaned;

  const lower = cleaned.toLowerCase();
  if (
    lower.startsWith("http://") ||
    lower.startsWith("https://") ||
    lower.startsWith("mailto:")
  ) {
    return cleaned;
  }
  return null;
}
